import { Component, input, output } from '@angular/core';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../../ui/button/button';
import { Cube01 } from '../../icons/cube-01/cube-01';
import { Check } from '../../icons/check/check';
import { CurrencyEuroCircle } from '../../icons/currency-euro-circle/currency-euro-circle';
import { Stars02 } from '../../icons/stars-02/stars-02';
import { InfoCircle } from '../../icons/info-circle/info-circle';
import { Clock } from '../../icons/clock/clock';
import { formatPrice } from '../../functions/functions';

export interface PackageCardSection {
  title: string;
  /** Benefits, shown with a green check. */
  items: string[];
  /** Restrictions and charges, shown with a neutral info icon after the benefits. */
  rules?: string[];
}

/** Price-list card shared by the individual and company package pages (mirrors fullscale.rs/paketi). */
@Component({
  standalone: true,
  selector: 'app-package-card',
  imports: [ButtonComponent, Cube01, Check, CurrencyEuroCircle, Stars02, InfoCircle, Clock],
  templateUrl: './package-card.html',
  styleUrl: './package-card.scss',
  // Rows this card spans in the page grid: header, price, hours, rate, button, divider + sections.
  host: { '[style.--card-rows]': '6 + sections().length' },
})
export class PackageCard {
  name = input.required<string>();
  price = input<number>();
  hours = input<number>();
  pricePerHour = input<number>();
  /** Amount saved compared to the most expensive hourly rate; hidden when 0. */
  savings = input<number>(0);
  isPopular = input<boolean>(false);
  /** Express: bookable only as a Last-Minute slot (next 24h); shown as a badge like "Popularan". */
  isLastMinuteOnly = input<boolean>(false);
  sections = input<PackageCardSection[]>([]);
  isDisabled = input<boolean>(false);
  isBusy = input<boolean>(false);

  orderClicked = output<void>();

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
  protected readonly formatPrice = formatPrice;
}

/** Savings per package: `(highest hourly rate − this rate) × hours`, matching the public price list. */
export function getPackageSavings(
  pkg: { hours?: number; pricePerHour?: number },
  all: { pricePerHour?: number }[],
): number {
  const maxRate = Math.max(0, ...all.map((p) => p.pricePerHour ?? 0));
  return Math.max(0, (maxRate - (pkg.pricePerHour ?? maxRate)) * (pkg.hours ?? 0));
}

/** The public price list highlights the middle package as "Popularan". */
export function isPopularPackage(index: number, count: number): boolean {
  return count >= 3 && index === Math.floor(count / 2);
}
